import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe, Ticket, Wifi, Contact, Mail, MessageSquare, Phone, Type,
  Download, CheckCircle2, AlertTriangle, Loader2, Upload, X,
} from "lucide-react";
import { InstaPassLogo } from "../Components/layout/Components/InstaPassLogo";
import { QR_TYPES, EMPTY_FIELDS, buildPayload, suggestName } from "../Components/QRStudio/qrPayload";
import {
  PATTERNS, CORNERS, PRESETS, colorAdvice, renderQrSvg, scanCheck, downloadPng, downloadSvg,
} from "../Components/QRStudio/qrRender";

const RED = "#E52324";
const ICONS = { url: Globe, event: Ticket, wifi: Wifi, vcard: Contact, email: Mail, sms: MessageSquare, phone: Phone, text: Type };
const CAPTIONS = ["", "Scan for tickets", "Scan me", "Scan to check in", "Join our Wi-Fi", "Save my contact"];

const field =
  "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3.5 py-2.5 text-[14px] text-white placeholder-white/25 outline-none focus:border-[#E52324]/70 focus:bg-white/[0.06] transition";
const labelCls = "block text-[12px] text-white/55 mb-1.5 font-semibold";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-white/35 mt-1">{hint}</span>}
    </label>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 p-1 rounded-lg bg-white/[0.04] border border-white/10">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`py-2 rounded-md text-[13px] font-semibold transition ${
            value === o.id ? "bg-white text-[#0B0F19]" : "text-white/60 hover:text-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Section({ step, title, children }) {
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-7 h-7 rounded-full grid place-items-center text-[12px] font-bold bg-[#E52324]/15 text-[#FF6B6B]">{step}</span>
        <h2 className="text-[16px] text-white font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

// Downscale an uploaded logo to a small PNG data URL so the SVG stays light.
function readLogo(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const max = 256;
        const k = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.width * k));
        c.height = Math.max(1, Math.round(img.height * k));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function TypeFields({ type, f, set }) {
  const on = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  switch (type) {
    case "url":
      return (
        <Field label="Web address">
          <input className={field} value={f.url} onChange={on("url")} placeholder="instapass.shop" inputMode="url" autoFocus />
        </Field>
      );
    case "event":
      return (
        <Field label="Event link" hint="Open your event on InstaPass and copy the address from the browser bar.">
          <input className={field} value={f.url} onChange={on("url")} placeholder="instapass.shop/tickets/group.details/…" inputMode="url" />
        </Field>
      );
    case "wifi":
      return (
        <div className="grid gap-4">
          <Field label="Network name">
            <input className={field} value={f.ssid} onChange={on("ssid")} placeholder="Venue-Guest" />
          </Field>
          <Field label="Security">
            <select className={field} value={f.security} onChange={on("security")}>
              <option value="WPA">WPA / WPA2 / WPA3</option>
              <option value="WEP">WEP</option>
              <option value="nopass">No password</option>
            </select>
          </Field>
          {f.security !== "nopass" && (
            <Field label="Password">
              <input className={field} value={f.password} onChange={on("password")} autoComplete="off" />
            </Field>
          )}
          <label className="flex items-center gap-2 text-[13px] text-white/60">
            <input type="checkbox" checked={f.hidden} onChange={on("hidden")} className="accent-[#E52324]" /> Hidden network
          </label>
        </div>
      );
    case "vcard":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="First name"><input className={field} value={f.first} onChange={on("first")} /></Field>
          <Field label="Last name"><input className={field} value={f.last} onChange={on("last")} /></Field>
          <Field label="Company"><input className={field} value={f.org} onChange={on("org")} /></Field>
          <Field label="Job title"><input className={field} value={f.title} onChange={on("title")} /></Field>
          <Field label="Mobile"><input className={field} value={f.phone} onChange={on("phone")} inputMode="tel" /></Field>
          <Field label="Email"><input className={field} value={f.email} onChange={on("email")} inputMode="email" /></Field>
          <Field label="Website"><input className={field} value={f.website} onChange={on("website")} inputMode="url" /></Field>
          <Field label="Address"><input className={field} value={f.address} onChange={on("address")} /></Field>
        </div>
      );
    case "email":
      return (
        <div className="grid gap-4">
          <Field label="Send to"><input className={field} value={f.to} onChange={on("to")} inputMode="email" placeholder="hello@yourbrand.com" /></Field>
          <Field label="Subject"><input className={field} value={f.subject} onChange={on("subject")} /></Field>
          <Field label="Message"><textarea className={`${field} min-h-[90px]`} value={f.body} onChange={on("body")} /></Field>
        </div>
      );
    case "sms":
      return (
        <div className="grid gap-4">
          <Field label="Phone number"><input className={field} value={f.phone} onChange={on("phone")} inputMode="tel" /></Field>
          <Field label="Message"><textarea className={`${field} min-h-[90px]`} value={f.message} onChange={on("message")} /></Field>
        </div>
      );
    case "phone":
      return (
        <Field label="Phone number"><input className={field} value={f.phone} onChange={on("phone")} inputMode="tel" /></Field>
      );
    default:
      return (
        <Field label="Text">
          <textarea className={`${field} min-h-[120px]`} value={f.text} onChange={on("text")} />
        </Field>
      );
  }
}

export default function QRGeneratorPage() {
  const [type, setType] = useState("url");
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [fg, setFg] = useState("#000000");
  const [bg, setBg] = useState("#FFFFFF");
  const [pattern, setPattern] = useState("square");
  const [corner, setCorner] = useState("square");
  const [logoMode, setLogoMode] = useState("none"); // none | instapass | custom
  const [logoData, setLogoData] = useState(null);
  const [caption, setCaption] = useState("");
  const [check, setCheck] = useState({ state: "idle" }); // idle | checking | ok | fail
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const f = fields[type];
  const setF = (v) => setFields((all) => ({ ...all, [type]: v }));
  const { payload, error, warning } = buildPayload(type, f);
  const logo = logoMode === "instapass" ? "instapass" : logoMode === "custom" ? logoData : null;
  const advice = colorAdvice(fg, bg);

  const render = useMemo(() => {
    const text = payload || "https://instapass.shop";
    try {
      return { ...renderQrSvg({ payload: text, fg, bg, pattern, corner, logo, label: caption }), placeholder: !payload };
    } catch (e) {
      return { error: "That's too much to fit in one QR code. Shorten it, or use a link instead." };
    }
  }, [payload, fg, bg, pattern, corner, logo, caption]);

  // Read the finished code back, like a phone would, before anyone prints it.
  useEffect(() => {
    if (!payload || render.error) {
      setCheck({ state: "idle" });
      return undefined;
    }
    let live = true;
    setCheck({ state: "checking" });
    const t = setTimeout(() => {
      scanCheck(render.svg, render.aspect, payload)
        .then((r) => live && setCheck({ state: r.ok ? "ok" : "fail" }))
        .catch(() => live && setCheck({ state: "fail" }));
    }, 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [payload, render]);

  const ready = Boolean(payload) && !render.error;
  const name = suggestName(type, f);

  async function onLogo(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setLogoData(await readLogo(file));
      setLogoMode("custom");
    } catch {
      setLogoMode("none");
    }
    e.target.value = "";
  }

  async function onPng() {
    setBusy(true);
    try {
      await downloadPng(render.svg, render.aspect, name, 1200);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="h-[64px] bg-[#0B0F19]/85 backdrop-blur-xl border-b border-white/[0.05] flex items-center justify-between px-4 sm:px-6 lg:px-10 sticky top-0 z-40">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" className="shrink-0"><InstaPassLogo size="sm" /></Link>
          <span className="hidden sm:inline text-white/15">|</span>
          <Link to="/qr-studio" className="hidden sm:inline text-[13px] text-white/50 hover:text-white/80 font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            QR Code Studio
          </Link>
        </div>
        <Link to="/qr-studio" className="text-[12px] font-semibold text-[#E52324] hover:text-[#FF4444]">← Overview</Link>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-[28px] sm:text-[36px] font-extrabold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            QR Code Generator
          </h1>
          <p className="text-white/50 text-[14px] mt-2 max-w-xl">
            Free, no sign-up, no watermark. Every code is test-scanned before you download it.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8 items-start">
          <div className="grid gap-5 min-w-0">
            <Section step="1" title="What should it open?">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                {QR_TYPES.map((t) => {
                  const Icon = ICONS[t.id];
                  const on = t.id === type;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setType(t.id)}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-semibold border transition text-left ${
                        on ? "border-[#E52324] bg-[#E52324]/10 text-white" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                      }`}
                    >
                      <Icon size={16} className={on ? "text-[#FF6B6B]" : "text-white/40"} />
                      <span className="truncate">{t.label}</span>
                    </button>
                  );
                })}
              </div>
              <TypeFields type={type} f={f} set={setF} />
              {error && <p className="mt-3 text-[13px] text-[#FF6B6B]">{error}</p>}
              {!error && warning && <p className="mt-3 text-[13px] text-amber-300/80">{warning}</p>}
            </Section>

            <Section step="2" title="Make it yours">
              <div className="grid gap-5">
                <div>
                  <span className={labelCls}>Colors</span>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setFg(p.fg); setBg(p.bg); }}
                        className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-[12px] font-semibold border transition ${
                          fg === p.fg && bg === p.bg ? "border-white/60 text-white" : "border-white/10 text-white/55 hover:text-white"
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full grid place-items-center border border-white/20" style={{ background: p.bg }}>
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.fg }} />
                        </span>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[["Code", fg, setFg], ["Background", bg, setBg]].map(([l, v, s]) => (
                      <label key={l} title={l} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-2">
                        <input type="color" value={v} onChange={(e) => s(e.target.value.toUpperCase())} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0" />
                        <span className="text-[12px] text-white/45 hidden sm:inline">{l}</span>
                        <span className="ml-auto text-[12px] font-mono text-white/70">{v}</span>
                      </label>
                    ))}
                  </div>
                  {advice && (
                    <p className={`mt-2 text-[12px] flex gap-1.5 ${advice.level === "error" ? "text-[#FF6B6B]" : "text-amber-300/80"}`}>
                      <AlertTriangle size={14} className="shrink-0 mt-px" /> {advice.text}
                    </p>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <span className={labelCls}>Dots</span>
                    <Segmented options={PATTERNS} value={pattern} onChange={setPattern} />
                  </div>
                  <div>
                    <span className={labelCls}>Corners</span>
                    <Segmented options={CORNERS} value={corner} onChange={setCorner} />
                  </div>
                </div>
                <div>
                  <span className={labelCls}>Logo in the middle</span>
                  <div className="flex flex-wrap gap-2">
                    {[["none", "None"], ["instapass", "InstaPass"]].map(([id, l]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setLogoMode(id)}
                        className={`rounded-lg px-3.5 py-2 text-[13px] font-semibold border transition ${
                          logoMode === id ? "border-white/60 bg-white/[0.06] text-white" : "border-white/10 text-white/55 hover:text-white"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileRef.current && fileRef.current.click()}
                      className={`rounded-lg px-3.5 py-2 text-[13px] font-semibold border transition flex items-center gap-2 ${
                        logoMode === "custom" ? "border-white/60 bg-white/[0.06] text-white" : "border-white/10 text-white/55 hover:text-white"
                      }`}
                    >
                      {logoMode === "custom" && logoData ? (
                        <img src={logoData} alt="" className="w-5 h-5 object-contain rounded" />
                      ) : (
                        <Upload size={14} />
                      )}
                      {logoMode === "custom" ? "Your logo" : "Upload logo"}
                    </button>
                    {logoMode === "custom" && (
                      <button type="button" onClick={() => { setLogoMode("none"); setLogoData(null); }} className="text-white/40 hover:text-white p-2" aria-label="Remove logo">
                        <X size={14} />
                      </button>
                    )}
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onLogo} />
                  </div>
                </div>
                <div>
                  <span className={labelCls}>Caption under the code</span>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {CAPTIONS.map((c) => (
                      <button
                        key={c || "none"}
                        type="button"
                        onClick={() => setCaption(c)}
                        className={`rounded-full px-3 py-1.5 text-[12px] font-semibold border transition ${
                          caption === c ? "border-white/60 text-white" : "border-white/10 text-white/50 hover:text-white"
                        }`}
                      >
                        {c || "None"}
                      </button>
                    ))}
                  </div>
                  <input className={field} value={caption} maxLength={32} onChange={(e) => setCaption(e.target.value)} placeholder="Or write your own" />
                </div>
              </div>
            </Section>
          </div>

          <aside className="lg:sticky lg:top-[88px]">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
              <div className="rounded-xl bg-white p-5 grid place-items-center">
                {render.error ? (
                  <p className="text-[13px] text-[#B91C1C] text-center py-16 px-4">{render.error}</p>
                ) : (
                  <div
                    className={`w-full max-w-[300px] transition-opacity [&>svg]:w-full [&>svg]:h-auto ${render.placeholder ? "opacity-20" : "opacity-100"}`}
                    // SVG is built locally from escaped values (see qrRender.js)
                    dangerouslySetInnerHTML={{ __html: render.svg }}
                  />
                )}
              </div>

              <div className="mt-4 min-h-[44px] flex items-center gap-2.5 text-[13px]">
                {!payload && !render.error && <span className="text-white/45">Fill in step 1 to make your code.</span>}
                {check.state === "checking" && (
                  <span className="flex items-center gap-2 text-white/60"><Loader2 size={16} className="animate-spin" /> Test-scanning…</span>
                )}
                {check.state === "ok" && (
                  <span className="flex items-center gap-2 text-emerald-400 font-semibold"><CheckCircle2 size={17} /> Scans correctly</span>
                )}
                {check.state === "fail" && (
                  <span className="flex items-start gap-2 text-[#FF6B6B]">
                    <AlertTriangle size={17} className="shrink-0" /> Our test scan couldn't read this design. Try more contrast, square dots or no logo.
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2.5 mt-1">
                <button
                  type="button"
                  disabled={!ready || busy}
                  onClick={onPng}
                  className="flex items-center justify-center gap-2 rounded-lg py-3 text-[14px] font-bold text-white disabled:opacity-35 disabled:cursor-not-allowed transition hover:brightness-110"
                  style={{ background: RED }}
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} PNG
                </button>
                <button
                  type="button"
                  disabled={!ready}
                  onClick={() => downloadSvg(render.svg, name)}
                  className="flex items-center justify-center gap-2 rounded-lg py-3 text-[14px] font-bold border border-white/15 text-white hover:bg-white/[0.06] disabled:opacity-35 disabled:cursor-not-allowed transition"
                >
                  <Download size={16} /> SVG
                </button>
              </div>
              <p className="text-[11px] text-white/35 mt-3 leading-relaxed">
                PNG is 1200px for screens and small print. SVG stays sharp at any size — use it for posters and banners.
                {ready && !render.placeholder && ` Version ${render.version}, error correction ${render.ecLevel}.`}
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
